// Named Oddin environments and the API base URL each one means.
//
// WHY THESE ARE PINNED HERE, since pinning an endpoint into a published
// package is normally the wrong instinct: a hand-written `baseUrl` is
// invisible when it is wrong. A typo, a stale env var, or the wrong
// environment produces a player that either fails in a way that looks like an
// outage, or — worse — works while quietly losing everything derived from the
// host. The beacons endpoint is derived from `baseUrl` by hostname convention
// (`feed[-dev].<domain>` → `beacons[-dev].<domain>`), so an off-convention
// baseUrl means QoE analytics is off; an integrator who selects `env` instead
// cannot land in that state at all, because the base is a feed host by
// construction.
//
// AND WHY `baseUrl` SURVIVES ANYWAY. Pinning couples the endpoint to a
// release, and this SDK is consumed by integrators who self-host a pinned
// build and upgrade rarely. If these constants were the only way to reach the
// platform, a domain change would strand every one of them. `baseUrl` is
// therefore not deprecated: it is the escape hatch for that, for a customer
// whose own CDN or gateway fronts the streams API, and for the CN plane,
// whose ICP-registered host is not under this domain and rotates.
//
// Exactly one of the two is the right answer for any given integration, which
// is why the callers reject being given both rather than picking a winner.

import { PlaybackError } from './errors';

/**
 * A named Oddin environment.
 *
 * - `integration` — the shared pre-production stack. Where customers build
 *   and test against real matches.
 * - `production` — the production stack. A separate distribution, not an
 *   alias of integration.
 */
export type HavikEnv = 'integration' | 'production';

const BASE_URLS: Readonly<Record<HavikEnv, string>> = {
  integration: 'https://feed-dev.oddin-video.gg',
  production: 'https://feed.oddin-video.gg',
};

/**
 * The API base URL for a named environment.
 *
 * Mode A takes {@link HavikEnv} directly (`createPlayer({ env: 'production' })`).
 * Mode B and Mode C still take a `baseUrl`, so call this to get one rather
 * than copying a hostname into your own config:
 *
 * ```ts
 * resolveStream({ baseUrl: baseUrlForEnv('production'), matchUrn, credential });
 * ```
 *
 * Throws on an unrecognized value rather than returning undefined — callers
 * reach this from plain JS where the type gives no protection, and a silent
 * undefined becomes a relative URL against the embedding page.
 */
export function baseUrlForEnv(env: HavikEnv): string {
  // Own-property lookup, not a bare index. A bare BASE_URLS['constructor']
  // resolves an INHERITED Object.prototype property and returns a FUNCTION,
  // which is truthy — so the guard below would pass it through, createPlayer
  // would carry a function as its base URL, and joinUrl would die on
  // `base.replace is not a function`: a TypeError from deep inside the SDK
  // instead of the typed error naming the two valid values. `toString` and
  // `__proto__` do the same. Verified before fixing: all three returned
  // without throwing.
  //
  // hasOwnProperty.call, not Object.hasOwn — the global build targets
  // firefox91/safari15 (vite.global.config.ts) and Object.hasOwn needs
  // Firefox 92 / Safari 15.4, which Vite downlevels syntax for but does not
  // polyfill. Same guard and same reasoning as core/environment.ts.
  const url = Object.prototype.hasOwnProperty.call(BASE_URLS, env) ? BASE_URLS[env] : undefined;
  if (!url) {
    throw new PlaybackError(
      'INTERNAL',
      0,
      `unknown env ${JSON.stringify(env)} — expected 'integration' or 'production'`,
    );
  }
  return url;
}
